/**
 * Pure helpers for Engage shadow beer-profile comparison (ITT-970).
 */

export const BEER_PROFILE_COMPARISON_FIELDS = ['name', 'style', 'abv', 'description'];

export const BEER_PROFILE_COMPARISON_OUTCOMES = [
  'unchanged',
  'proposed',
  'unavailable',
  'conflicted',
];

/**
 * @param {string | null | undefined} outcome
 * @returns {string}
 */
export function beerProfileOutcomeLabel(outcome) {
  if (outcome === 'unchanged') {
    return 'Unchanged';
  }
  if (outcome === 'proposed') {
    return 'Proposed';
  }
  if (outcome === 'unavailable') {
    return 'Unavailable';
  }
  if (outcome === 'conflicted') {
    return 'Conflicted';
  }
  return 'Unknown';
}

/**
 * MUI Chip color for a comparison outcome.
 *
 * @param {string | null | undefined} outcome
 * @returns {'default' | 'success' | 'warning' | 'error' | 'info'}
 */
export function beerProfileOutcomeColor(outcome) {
  if (outcome === 'unchanged') {
    return 'success';
  }
  if (outcome === 'proposed') {
    return 'warning';
  }
  if (outcome === 'conflicted') {
    return 'error';
  }
  if (outcome === 'unavailable') {
    return 'default';
  }
  return 'default';
}

/**
 * Formats a profile field value for operator display.
 *
 * @param {unknown} value
 * @param {string} field
 * @returns {string}
 */
export function formatBeerProfileFieldValue(value, field) {
  if (field === 'abv') {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return String(value);
    }
    return '—';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : '—';
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return String(value);
  }
  return '—';
}

/**
 * Field-source evidence label for operators.
 *
 * @param {{ kind?: string, id?: string } | null | undefined} source
 * @returns {string}
 */
export function formatBeerProfileFieldSource(source) {
  if (!source || typeof source !== 'object') {
    return '—';
  }
  const kind = typeof source.kind === 'string' ? source.kind : '';
  const sourceId = typeof source.id === 'string' ? source.id : '';
  if (!kind && !sourceId) {
    return '—';
  }
  if (kind && sourceId) {
    return `${kind}:${sourceId}`;
  }
  return kind || sourceId;
}

/**
 * @param {unknown} conflicts
 * @returns {Array<{ field: string, reason: string, observationIds: string[], overrideId: string | null }>}
 */
export function normalizeBeerProfileConflicts(conflicts) {
  if (!Array.isArray(conflicts)) {
    return [];
  }
  return conflicts
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      field: typeof item.field === 'string' ? item.field : 'unknown',
      reason: typeof item.reason === 'string' ? item.reason : 'unknown',
      observationIds: Array.isArray(item.observationIds)
        ? item.observationIds.filter((observationId) => typeof observationId === 'string')
        : [],
      overrideId: typeof item.overrideId === 'string' ? item.overrideId : null,
    }));
}

/** Engage brewery beer-list shadow discoverability filters. */
export const SHADOW_PROFILE_FILTER_ALL = 'all';
export const SHADOW_PROFILE_FILTER_HAS_SHADOW = 'hasShadow';
export const SHADOW_PROFILE_FILTER_HAS_PROPOSED = 'hasProposedDiffs';
export const SHADOW_PROFILE_FILTER_HAS_CONFLICTS = 'hasConflicts';

/** Firestore `in` query chunk size (API max 30). */
export const BEER_PROFILE_VERSION_IN_CHUNK_SIZE = 30;

/**
 * Coerce Firestore Timestamp / Date / millis for latest-version reduce.
 *
 * @param {unknown} value
 * @returns {number}
 */
export function beerProfileBuiltAtMillis(value) {
  if (value == null) {
    return 0;
  }
  if (typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  if (typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanos / 1e6);
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  return 0;
}

/**
 * Derive list-level shadow flags from the latest beerProfileVersions doc.
 * Missing `comparison` (pre-ITT-970) → no proposed diffs (still hasShadow).
 *
 * @param {Record<string, unknown> | null | undefined} version
 * @returns {{
 *   beerId: string | null,
 *   versionId: string | null,
 *   hasShadow: boolean,
 *   hasComparison: boolean,
 *   hasProposedDiffs: boolean,
 *   hasConflicts: boolean,
 *   proposedFieldCount: number,
 * }}
 */
export function deriveBeerProfileShadowSummary(version) {
  if (!version || typeof version !== 'object') {
    return {
      beerId: null,
      versionId: null,
      hasShadow: false,
      hasComparison: false,
      hasProposedDiffs: false,
      hasConflicts: false,
      proposedFieldCount: 0,
    };
  }

  const beerId = typeof version.beerId === 'string' ? version.beerId : null;
  const versionId = typeof version.id === 'string' ? version.id : null;
  const comparison = version.comparison;
  const hasComparison = comparison != null && typeof comparison === 'object';
  const fields = hasComparison && comparison.fields && typeof comparison.fields === 'object'
    ? comparison.fields
    : null;

  let proposedFieldCount = 0;
  let hasConflictedOutcome = false;
  if (fields) {
    BEER_PROFILE_COMPARISON_FIELDS.forEach((fieldKey) => {
      const fieldEntry = fields[fieldKey];
      const outcome = fieldEntry && typeof fieldEntry === 'object'
        ? fieldEntry.outcome
        : null;
      if (outcome === 'proposed') {
        proposedFieldCount += 1;
      }
      if (outcome === 'conflicted') {
        hasConflictedOutcome = true;
      }
    });
  }

  const normalizedConflicts = normalizeBeerProfileConflicts(version.conflicts);
  const hasConflicts = normalizedConflicts.length > 0 || hasConflictedOutcome;

  return {
    beerId,
    versionId,
    hasShadow: true,
    hasComparison,
    hasProposedDiffs: hasComparison && proposedFieldCount > 0,
    hasConflicts,
    proposedFieldCount,
  };
}

/**
 * Keep the latest version per beerId by builtAt (Approach A client reduce).
 *
 * @param {Array<Record<string, unknown>>} versions
 * @returns {Map<string, Record<string, unknown>>}
 */
export function reduceLatestBeerProfileVersionsByBeerId(versions) {
  const latestByBeerId = new Map();
  if (!Array.isArray(versions)) {
    return latestByBeerId;
  }
  versions.forEach((version) => {
    if (!version || typeof version !== 'object') {
      return;
    }
    const beerId = typeof version.beerId === 'string' ? version.beerId.trim() : '';
    if (!beerId) {
      return;
    }
    const existing = latestByBeerId.get(beerId);
    if (!existing) {
      latestByBeerId.set(beerId, version);
      return;
    }
    if (beerProfileBuiltAtMillis(version.builtAt) > beerProfileBuiltAtMillis(existing.builtAt)) {
      latestByBeerId.set(beerId, version);
    }
  });
  return latestByBeerId;
}

/**
 * @param {Iterable<string> | null | undefined} beerIds
 * @param {Map<string, ReturnType<typeof deriveBeerProfileShadowSummary>>} summariesByBeerId
 * @returns {{
 *   shadowCount: number,
 *   proposedDiffCount: number,
 *   conflictCount: number,
 * }}
 */
export function countBeerProfileShadowSummaries(beerIds, summariesByBeerId) {
  let shadowCount = 0;
  let proposedDiffCount = 0;
  let conflictCount = 0;
  const ids = beerIds == null ? [] : Array.from(beerIds);
  ids.forEach((beerId) => {
    const summary = summariesByBeerId?.get(beerId);
    if (!summary?.hasShadow) {
      return;
    }
    shadowCount += 1;
    if (summary.hasProposedDiffs) {
      proposedDiffCount += 1;
    }
    if (summary.hasConflicts) {
      conflictCount += 1;
    }
  });
  return { shadowCount, proposedDiffCount, conflictCount };
}

/**
 * @param {ReturnType<typeof deriveBeerProfileShadowSummary> | null | undefined} summary
 * @param {string} filter
 * @returns {boolean}
 */
export function beerMatchesShadowProfileFilter(summary, filter) {
  if (filter === SHADOW_PROFILE_FILTER_ALL || !filter) {
    return true;
  }
  if (filter === SHADOW_PROFILE_FILTER_HAS_SHADOW) {
    return Boolean(summary?.hasShadow);
  }
  if (filter === SHADOW_PROFILE_FILTER_HAS_PROPOSED) {
    return Boolean(summary?.hasProposedDiffs);
  }
  if (filter === SHADOW_PROFILE_FILTER_HAS_CONFLICTS) {
    return Boolean(summary?.hasConflicts);
  }
  return true;
}

/**
 * Split beer ids into Firestore `in`-safe chunks.
 *
 * @param {string[]} beerIds
 * @param {number} [chunkSize]
 * @returns {string[][]}
 */
export function chunkBeerIdsForProfileVersionQuery(
  beerIds,
  chunkSize = BEER_PROFILE_VERSION_IN_CHUNK_SIZE,
) {
  const uniqueIds = [];
  const seen = new Set();
  (Array.isArray(beerIds) ? beerIds : []).forEach((beerId) => {
    if (typeof beerId !== 'string') {
      return;
    }
    const trimmed = beerId.trim();
    if (!trimmed || seen.has(trimmed)) {
      return;
    }
    seen.add(trimmed);
    uniqueIds.push(trimmed);
  });
  const size = typeof chunkSize === 'number' && chunkSize > 0
    ? chunkSize
    : BEER_PROFILE_VERSION_IN_CHUNK_SIZE;
  const chunks = [];
  for (let index = 0; index < uniqueIds.length; index += size) {
    chunks.push(uniqueIds.slice(index, index + size));
  }
  return chunks;
}
